import { PLANS } from '@aeon-karaoke/cloud-convex/model/plans';
import type { BillingSummary, CheckoutVariant } from '@/hook';
import { formatUsd } from '@/lib/money';
import { formatDateWithYear, formatLongDate } from '@/lib/time';

export interface CheckoutLine {
  k: string;
  v: string;
  tone: 'ink' | 'mute' | 'up' | 'down';
  strong?: boolean;
}

export interface CheckoutSheet {
  kicker: string;
  title: string;
  lines: CheckoutLine[];
  dueCents: number;
  cta: string;
  fine: string;
  /** The deployment's terms, linked from the fine print once it names them. */
  termsUrl: string | null;
  /** The confirm ends on Stripe's page; otherwise the change happens here. */
  redirects: boolean;
}

const addMonths = (ms: number, months: number) => {
  const date = new Date(ms);
  date.setMonth(date.getMonth() + months);
  return date.getTime();
};

/**
 * Everything a checkout variant shows, from the plan summary: the lines,
 * what is due today, the button and the fine print. Pure, so the copy for
 * the three variants is checked by checkoutSheet.spec.ts.
 */
export function checkoutSheet(variant: CheckoutVariant, summary: BillingSummary, now = Date.now()): CheckoutSheet {
  return { ...sheetFor(variant, summary, now), termsUrl: summary.checkout.termsUrl };
}

function sheetFor(variant: CheckoutVariant, summary: BillingSummary, now: number): Omit<CheckoutSheet, 'termsUrl'> {
  const current = summary.plan.name;
  const { perPlan } = summary.packs;
  const tax: CheckoutLine = { k: 'Tax', v: '$0.00', tone: 'mute' };
  const periodEnd = summary.renewsAt ?? summary.endsAt ?? now;

  switch (variant.kind) {
    case 'beta': {
      const price = PLANS.beta.priceCents.month;
      return {
        kicker: 'Checkout',
        title: `${PLANS.beta.name}, monthly`,
        lines: [
          { k: `${PLANS.beta.name} monthly`, v: formatUsd(price), tone: 'ink', strong: true },
          { k: 'Packs unlocked', v: String(perPlan.beta), tone: 'up', strong: true },
          { k: 'Tracks a month', v: String(PLANS.beta.monthlyCreateLimit), tone: 'up', strong: true },
          tax,
        ],
        dueCents: price,
        cta: `Confirm and pay ${formatUsd(price)}`,
        fine: `Renews monthly on ${formatLongDate(addMonths(now, 1))}. Cancel any time from settings.`,
        redirects: true,
      };
    }
    case 'cancel':
      return {
        kicker: 'Cancel',
        title: 'Cancel subscription',
        lines: [
          { k: `${current} until`, v: formatDateWithYear(periodEnd), tone: 'ink', strong: true },
          { k: 'Then', v: PLANS.free.name, tone: 'mute' },
          { k: 'Packs you lose', v: String(perPlan[summary.tier]), tone: 'down', strong: true },
          { k: 'Tracks you created', v: 'yours forever', tone: 'up', strong: true },
        ],
        dueCents: 0,
        cta: 'Confirm cancellation',
        fine: `Nothing is charged today. You stay on ${current} until ${formatLongDate(periodEnd)}, then move to ${PLANS.free.name}.`,
        redirects: false,
      };
    case 'pack':
      return {
        kicker: 'Pack',
        title: variant.pack.name,
        lines: [
          { k: variant.pack.name, v: formatUsd(variant.pack.priceCents), tone: 'ink', strong: true },
          { k: 'Songs', v: String(variant.pack.songCount), tone: 'up', strong: true },
          tax,
        ],
        dueCents: variant.pack.priceCents,
        cta: `Confirm and pay ${formatUsd(variant.pack.priceCents)}`,
        fine: 'Yours to keep, with or without a subscription.',
        redirects: true,
      };
  }
}
